import type { BuilderWho, ClientProcessDto, ClientProcessStepDto, PhaseKey } from '@nsb/core';
import { dateWords, yearRule, type YearRule } from '../../days.ts';
import { bandOf, type BandFacts } from './bands.ts';
import { gateDone } from './passed.ts';
import { earlyFor, opensFrom, stepName, tileWords, type Band, type Standing, type TilePhase, type TileStep } from './model.ts';

/**
 * A job's process as its client reads it (Jason, 2026-10-05: "the process tree
 * for their project with where things are and what is coming"), as tiles drawn
 * the way every tree is (design study 12): its steps at the top in their
 * phases, the band in the client's words (You, NSB, Others; client band A),
 * where each stands at its foot (states A: done, under way, theirs and reached
 * waiting on them, not started), and a step's own tasks in its card, ticked as
 * they are done (client tasks A). Read from what the API sends the client
 * (`ClientProcessDto`), nothing of NSB's own in it.
 */

/** A step opened, as the client reads it. */
export interface ClientCard {
  title: string;
  band: Band;
  picture: string | null;
  standing: Standing;
  foot: string;
  kind: ClientProcessStepDto['kind'];
  /** What it is, in the client's own note; none, and they see its tasks only. */
  what: string | null;
  tasks: { id: string; title: string; word: string; depth: number; done: boolean }[];
  /** What NSB shared of its log, its tasks' with it, newest first (design study 31, slice 3). */
  log: ClientProcessStepDto['log'];
}

const WHO_OF_CLIENT_OWNER: Record<ClientProcessStepDto['owner'] & string, BuilderWho> = { you: 'client', nsb: 'nsb', others: 'sub' };

/** What a step's band is drawn from, as the client has it (the one band rule, bands.ts, read in their words). */
const clientFacts = (s: Pick<ClientProcessStepDto, 'kind' | 'owner'>, end: 'start' | 'end' | null): BandFacts =>
  end ? { kind: end, who: null } : { kind: s.kind === 'inspection' ? 'work' : s.kind, who: s.owner ? WHO_OF_CLIENT_OWNER[s.owner] : null };

/**
 * Where a step stands for the client, and the words at its foot: their own, reached, waits on them (`waits`: it, or a
 * step of theirs inside it, is on their Waiting on you).
 */
function standingOf(s: ClientProcessStepDto, waits: boolean, year: YearRule): { standing: Standing; foot: string } {
  // A gate approved says by whom ("you" to the one who did), else that it is done (study 09, gates A); its day with its
  // year where the job runs over years or the day is not this year's (L-25a), as NSB's tiles write it.
  if (s.state === 'done' && s.kind === 'gate' && s.passed) return { standing: 'done', foot: gateDone(s.passed, s.doneOn, year) };
  if (s.state === 'done') return { standing: 'done', foot: `${s.word}${s.doneOn ? ` · ${dateWords(s.doneOn, 'dayOrDate', year)}` : ''}` };
  if (waits) return { standing: 'yours', foot: 'Waiting on you' };
  if (s.state === 'now') return { standing: 'now', foot: 'Under way' };
  if (s.state === 'next') return { standing: 'next', foot: s.word };
  return { standing: 'ahead', foot: 'Not started' };
}

/**
 * The tasks done in an earlier phase than their step (design study 31, slice 4; the API's `early`, core `earlyTasks`) as
 * the client's drawing and Working on have them: the outermost, one inside another being in its list.
 */
export function clientEarly(dto: Pick<ClientProcessDto, 'steps'>): ClientProcessStepDto[] {
  const byId = new Map(dto.steps.map((s) => [s.id, s]));
  const isEarly = (s: ClientProcessStepDto | undefined) => Boolean(s?.early && s.parentId !== null && byId.has(s.early.stepId));
  return dto.steps.filter((s) => isEarly(s) && !isEarly(byId.get(s.parentId!)));
}

/** The client's steps as tiles in the tree's phases, each step's card, and the lines between the steps, each once. */
export function clientTiles(dto: ClientProcessDto): { phases: TilePhase[]; steps: TileStep[]; cards: Map<string, ClientCard> } {
  const byId = new Map(dto.steps.map((s) => [s.id, s]));
  const top = dto.steps.filter((s) => s.parentId === null || !byId.has(s.parentId));
  const early = clientEarly(dto);
  const drawnEarly = new Set(early.map((s) => s.id));
  /** Where a step is drawn: itself at the top or done early, else in the step it is in. */
  const drawnOf = (id: string): string => {
    let s = byId.get(id);
    for (let i = 0; s && s.parentId !== null && !drawnEarly.has(s.id) && byId.has(s.parentId) && i < 100; i++) s = byId.get(s.parentId);
    return s?.id ?? id;
  };
  // A task's wait is its step's: the lines run between the steps; a task done early keeps its own, its step waiting on it.
  const needs = new Map<string, Set<string>>();
  const need = (to: string, from: string) => {
    if (from !== to && byId.has(from) && byId.has(to)) needs.set(to, new Set([...(needs.get(to) ?? []), from]));
  };
  for (const l of dto.links) need(drawnOf(l.stepId), drawnOf(l.needsId));
  for (const s of early) need(s.early!.stepId, s.id);
  // The phases with the step that closes each: the start of the drawing's transitions, and what a task done early with no
  // wait of its own hangs from (`opensFrom`).
  const closerKeys = new Set(dto.closers ?? []);
  const closerOf = (phase: PhaseKey) => top.find((s) => s.key !== null && closerKeys.has(s.key) && s.phase === phase)?.id ?? null;
  const closers = (dto.phases ?? []).map((p) => ({ key: p.key as string, closedBy: closerOf(p.key) }));
  const startId = top.find((s) => s.key !== null && s.key === dto.start)?.id ?? null;
  for (const s of early) {
    const from = needs.get(s.id)?.size ? null : opensFrom(closers, s.phase, startId);
    if (from) need(s.id, from);
  }
  const tasksOf = (id: string): ClientCard['tasks'] => {
    const out: ClientCard['tasks'] = [];
    const walk = (holder: string, depth: number) => {
      for (const t of dto.steps.filter((x) => x.parentId === holder)) {
        out.push({ id: t.id, title: t.title, word: t.word, depth, done: t.state === 'done' });
        walk(t.id, depth + 1);
      }
    };
    walk(id, 0);
    return out;
  };
  // A step waits on the client when it, or a step of theirs inside it, is on their Waiting on you.
  const waits = (id: string): boolean => Boolean(byId.get(id)?.yours) || dto.steps.some((c) => c.parentId === id && waits(c.id));
  const cards = new Map<string, ClientCard>();
  const year = yearRule(dto.today, dto.steps.map((s) => s.doneOn));
  const steps: TileStep[] = top.map((s) => {
    const end = s.key !== null && s.key === dto.start ? ('start' as const) : s.key === 'project.archive' ? ('end' as const) : null;
    const band = bandOf(clientFacts(s, end), 'client');
    const { standing, foot } = standingOf(s, waits(s.id), year);
    cards.set(s.id, { title: s.title, band, picture: s.icon, standing, foot, kind: s.kind, what: s.why, tasks: tasksOf(s.id), log: s.log ?? [] });
    return {
      key: s.id,
      phase: s.phase,
      needs: [...(needs.get(s.id) ?? [])],
      ...(end ? { end } : {}),
      title: s.title,
      band,
      picture: s.icon,
      standing,
      foot,
      words: tileWords(s.title, band, foot),
    };
  });
  // Each task done early as its small tile (design study 31, §7): its own band, its picture else its step's, its step
  // named under its name ("For Engineering"), where it stands at its foot in words as any tile's ("Waiting on you");
  // opened, its step's card (TreeView).
  for (const s of early) {
    const step = byId.get(s.early!.stepId)!;
    const band = bandOf(clientFacts(s, null), 'client');
    const { standing, foot } = standingOf(s, waits(s.id), year);
    const name = stepName(step.title);
    steps.push({
      key: s.id,
      phase: s.phase,
      needs: [...(needs.get(s.id) ?? [])],
      title: s.title,
      band,
      picture: s.icon ?? step.icon,
      standing,
      foot,
      early: { stepKey: step.id, stepName: name },
      words: tileWords(s.title, band, earlyFor(name), foot),
    });
  }
  const phases: TilePhase[] = (dto.phases ?? []).map((p, i) => {
    // Its steps: a task done early is its step's, never one of its phase's.
    const inIt = steps.filter((t) => t.phase === p.key && !t.end && !t.early);
    const done = inIt.filter((t) => t.standing === 'done').length;
    return { key: p.key, name: p.name, what: p.what, closedBy: closers[i]!.closedBy, facts: [`${inIt.length} ${inIt.length === 1 ? 'step' : 'steps'}`, inIt.length ? `${done} done` : ''].filter(Boolean) };
  });
  return { phases, steps, cards };
}
